import cn from 'classnames';
import { useMemo, useState } from 'react';
import { CloseForSearch, Search as SearchIcon } from '@shared/assets';
import { FavoriteIcon } from '@shared/assets/icons/favorite-icon.tsx';
import { FavoritePersonIcon } from '@shared/assets/icons/favorite-person-icon.tsx';
import { PersonIcon } from '@shared/assets/icons/person-icon.tsx';
import { IconSearch_26 } from '@shared/assets/icons/search_26.tsx';
import * as main from '@shared/styles/abstracts/vars.module.scss';
import { AuthorsListDropdownMenu } from '@shared/ui/Filter/AuthorList/dropdown-menu/dropdown-menu.tsx';
import { IconButton } from '@shared/ui/index.ts';
import { SearchInput } from '@shared/ui/inputs/searchInput/index.ts';
import { AuthorFavorite } from '@src/api/users.ts';
import * as st from './styles.module.scss';

interface Props {
  authors?: AuthorFavorite[];
  onAuthorClick: (id: number) => void;
  selectedAuthors: number[];
}

export const AuthorList = ({ authors, onAuthorClick, selectedAuthors }: Props): JSX.Element => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFavoriteActive, setIsFavoriteActive] = useState(false);
  const [search, setSearch] = useState('');

  const authorsWithMonogram = useMemo(() => {
    let output: Array<AuthorFavorite> = [];
    if (authors?.length) {
      output = authors;
    }
    if (!search && isSearchOpen) {
      return [];
    }
    if (search) {
      output = output.filter((a) => {
        return Boolean(
          a.first_name.toLowerCase().includes(search.toLowerCase()) ||
            a.middle_name.toLowerCase().includes(search.toLowerCase()) ||
            a.last_name.toLowerCase().includes(search.toLowerCase())
        );
      });
    }
    if (isFavoriteActive) {
      output = output.filter((a) => a.favorite);
    }

    return output;
  }, [authors, search, isFavoriteActive, isSearchOpen]);

  return (
    <>
      <IconButton
        onClick={() => setIsFavoriteActive((s) => !s)}
        style={{ position: 'absolute', top: 0, right: '41px' }}
        variant={isFavoriteActive ? 'activeDark' : 'transparent'}
        size="small"
      >
        <FavoriteIcon />
      </IconButton>
      {isSearchOpen ? (
        <SearchInput
          className={st.search}
          inputClassName={st.input}
          autoFocus={true}
          isOpen={true}
          isOpenInit={true}
          isClosable={true}
          isDelegatedControl={true}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftIcon={<SearchIcon style={{ color: main.colorTextSecondary, position: 'absolute' }} />}
          rightButtonWrapperClassName={st.closeSearchButton}
          leftButtonWrapperClassName={st.leftButtonWrapperClassName}
          rightIcon={
            <IconButton
              onClick={() => {
                setSearch('');
                setIsSearchOpen(false);
              }}
              variant={'dark'}
              size="small"
            >
              <CloseForSearch />
            </IconButton>
          }
        />
      ) : (
        <IconButton
          onClick={() => setIsSearchOpen(true)}
          style={{ position: 'absolute', top: 0, right: '72px' }}
          variant={'transparent'}
          size="small"
        >
          <IconSearch_26 />
        </IconButton>
      )}

      <section className={st.list}>
        {authorsWithMonogram?.map((author, i) => (
          <article
            className={cn(st.item, selectedAuthors.includes(author.id) && st.active)}
            onClick={() => onAuthorClick(author.id)}
            key={author.id}
          >
            <span className={st.icon}>
              {author.favorite ? <FavoritePersonIcon /> : <PersonIcon />}
            </span>
            <span className={st.title}>{author.full_initials}</span>

            <div className={st.dropdown}>
              <AuthorsListDropdownMenu
                authorId={author.id}
                isFavorite={author.favorite}
                needDropDawnShowUp={i > authorsWithMonogram.length - 3}
              />
            </div>
          </article>
        ))}
      </section>
    </>
  );
};
